//=============================================================================
//                       <--- Developed By Icon Team ---> 18 January 2021
//=============================================================================

// npm packages
import React, { useEffect, useContext, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// custom pages
import Context from 'context/context';
import auth from "apis/auth/auth";
import i18n from "i18n";
import { consentStyles } from './styles';

// material-ui
import { Typography, Grid } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import LanguageSharpIcon from '@material-ui/icons/LanguageSharp';


export default function DormantView() {
    const { state, dispatch } = useContext(Context);
    const { isLoggedIn, getDormantAccountStatus, reactivateDormantAccount } = auth();
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();
    const param = new URLSearchParams(location.search);
    const token = param.get("token");
    const [loading, setLoading] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [data, setData] = useState({
        loyaltyId: '',
        shipto: '',
        practiceName: '',
        org: 'Henry Schein Dental',
        token: '',
        error: false,
        errorText: ''
    });

    useEffect(() => {
        const finalToken = token || sessionStorage.getItem('token');
        handleGetDormantAccountStatus(finalToken);
        if (token) {
            history.replace({ search: '' });
        }
    }, []);

    const handleReactivateAPI = async () => {
        try {
            setBtnLoading(true);

            const finalToken = handleValidateToken(data?.token);
            if (!finalToken) {
                setData(prev => ({
                    ...prev,
                    error: true,
                    errorText: 'Your session has expired. Please sign in again to continue.'
                }));
                return;
            }

            const res = await reactivateDormantAccount({ token: finalToken });

            if (res?.status === 'SUCCESS') {
                await redirectToDashboard(finalToken);
            }

            else if (res?.status === 'ERROR') {
                handleApiResponseError(res.error_code);
            }
        } catch (error) {
            handleCatchBlockError(error);
        } finally {
            setBtnLoading(false);
        }
    }

    const handleGetDormantAccountStatus = async (token) => {
        try {
            setLoading(true);

            const finalToken = handleValidateToken(token);
            if (!finalToken) {
                setData(prev => ({
                    ...prev,
                    error: true,
                    errorText: 'Your session has expired. Please sign in again to continue.'
                }));
                return;
            }

            const res = await getDormantAccountStatus(finalToken);

            if (res?.status === 'SUCCESS') {
                sessionStorage.setItem('token', res?.data?.token);

                setData(prev => ({
                    ...prev,
                    loyaltyId: res?.data?.loyaltyId,
                    shipto: res?.data?.shipto,
                    practiceName: res?.data?.practiceName,
                    org: res?.data?.org,
                    token: res?.data?.token,
                    error: false,
                    errorText: ''
                }));
            }

            else if (res?.status === 'ERROR') {
                handleApiResponseError(res.error_code);
            }

        } catch (error) {
            handleCatchBlockError(error);
        } finally {
            setLoading(false);
        }
    }

    const handleValidateToken = (token) => {
        return token || sessionStorage.getItem('token') || null;
    }

    function handleRedirect() {
        if (data?.org === "Henry Schein Canada") {
            window.open('https://www.henryschein.ca', '_blank', 'noopener,noreferrer');
        } else {
            window.open('https://www.henryschein.com/en-us', '_blank', 'noopener,noreferrer');
        }
    }

    const handleApiResponseError = (errorCode) => {
        let errorMessage = 'Your session has expired. Please sign in again to continue.';
        sessionStorage.removeItem('token');
        setData(prev => ({
            ...prev,
            error: true,
            errorText: errorMessage
        }));
    }

    const handleCatchBlockError = (error) => {
        console.error('Unexpected error:', error);

        setData(prev => ({
            ...prev,
            error: true,
            errorText: 'Network error. Please check your connection and try again.'
        }));
    }

    const redirectToDashboard = async (token) => {
        const res1 = await isLoggedIn(token);

        if (res1?.user) {
            // Store data in global state
            dispatch({ type: "SET_ALA", payload: res1.loyaltyAccount });
            dispatch({ type: 'SET_USERINFO', payload: res1.user });
            dispatch({ type: "SET_ACA", payload: res1.customerAccount });

            // Navigate to dashboard
            history.push('/dashboard/mydashboard');
            return;
        }
    }

    return (
        <React.Fragment>
            <DormantViewUI
                loading={loading}
                btnLoading={btnLoading}
                data={data}
                handleReactivateAPI={handleReactivateAPI}
                handleRedirect={handleRedirect}
                t={t}
            />
        </React.Fragment>
    );
}

const DormantViewUI = ({ loading, btnLoading, data, handleReactivateAPI, handleRedirect, t }) => {
    const classes = consentStyles();

    const paragraphStyle = {
        textAlign: 'center',
        fontSize: 16
    };
    const buttonStyle = {
        fontSize: 16,
        background: '#0a72ba',
        border: 'none',
        color: '#fff',
        height: 35,
        width: 250,
        cursor: 'pointer',
        fontWeight: 600,
        letterSpacing: '0.05em'
    };
    const contentSection = {
        margin: "15px auto",
        background: "#fff",
        padding: "15px 0px 30px 0px",
        width: '60%',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        borderRadius: 10,
        lineHeight: 1.5,
        letterSpacing: '0.01em'
    }

    const errorAndLoadingCardStyle = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: '40px 20px',
        backgroundColor: '#fff',
        borderRadius: 10,
        margin: '20px auto',
        width: '60%',
        textAlign: 'center',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    }

    const horizontalLine = {
        border: 'none',
        height: 1,
        backgroundColor: '#ddd',
        margin: '10px 0px'
    }

    const Header = () => {
        return (
            <div style={{ margin: "0px 40px" }}>
                <div className={classes.gridContainer}>
                    <div className={classes.logoImgDiv} style={{ maxWidth: "83.3333%" }}>
                        <img
                            src="https://www.thriverewards.henryschein.com:8080/web/v1/static/images/logo.png"
                            className={classes.logoImg}
                            alt="Thrive Rewards Logo"
                        />
                    </div>
                    <div className={classes.flexBoxDiv}>
                        <Grid item className={classes.backToGrid}>
                            <IconButton
                                className={classes.globeIconBtn}
                                onClick={handleRedirect}
                            >
                                <LanguageSharpIcon className={classes.languageIcon} />
                            </IconButton>
                            <br />
                            <div
                                className={classes.backtoHendry}
                                onClick={handleRedirect}
                            >
                                {data?.org}
                            </div>
                        </Grid>
                    </div>
                </div>
            </div>
        )
    }

    const Loader = () => {
        return (
            <div style={errorAndLoadingCardStyle}>
                <h2 style={{ color: '#000', marginBottom: 20 }}>
                    Loading...
                </h2>
            </div>
        )
    }

    const ErrorPage = () => {
        return (
            <div
                style={errorAndLoadingCardStyle}
            >
                <h2 style={{ color: '#000', marginBottom: 20 }}>
                    {data?.errorText || 'Something went wrong!'}
                </h2>
            </div>
        )
    }

    const ContentBox = () => {
        return (
            <div style={contentSection}>
                <div style={{ textAlign: 'center', fontSize: 16 }}>
                    <span style={{ color: '#1890ff' }}>Account Status: </span>
                    <span style={{ fontWeight: 600 }}>Dormant</span>
                </div>
                <hr style={horizontalLine} />
                <div style={{ marginLeft: 30 }}>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>{data?.practiceName}</div>
                    <div style={{ fontSize: 13 }}>Member ID: {data?.loyaltyId}</div>
                    <div style={{ fontSize: 13 }}>Ship To ID: {data?.shipto}</div>
                </div>
                <hr style={horizontalLine} />
                <div style={{ fontSize: 22, fontWeight: 600, textAlign: 'center', marginTop: 10 }}>Your Thrive Rewards Account is Dormant</div><br />
                <p style={paragraphStyle}>Your account became dormant because rewards points were not redeemed in the past 24<br />months.</p>
                <p style={paragraphStyle}>Reactivating your account allows you to start earning Thrive Rewards points again and helps to ensure your<br />Thrive service benefits remain available.</p>
                <div style={{ textAlign: 'center' }}>
                    <button disabled={btnLoading} style={buttonStyle} onClick={handleReactivateAPI}>{btnLoading ? "Loading..." : "Activate My Account"}</button>
                </div><br />
                <hr style={horizontalLine} />
                <div style={{ marginLeft: 30 }}>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>Need Help?</div>
                    <div style={{ fontSize: 16 }}>If you have questions about your Thrive membership or need further assistance, our team is here to help.</div>
                    <Typography variant="body2" style={{ color: '#1f66b8', textDecoration: 'underline', marginTop: 10 }} component="p">
                        <a
                            href="https://outlook.office.com/mail/deeplink/compose?to=Thrive.Support@henryschein.com"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Contact Thrive Support
                        </a>
                    </Typography>
                </div>
            </div>
        )
    }

    return (
        <div className='container'>
            <Header />
            <div>
                {loading ?
                    <Loader /> :
                    data?.error ?
                        <ErrorPage /> :
                        (
                            data?.token &&
                            <ContentBox />
                        )
                }
            </div>
        </div>
    );
}

